/*
 * The reply agent's reads in the master inbox (master-inbox plan, steps 8c and 8d). Each query is
 * enabled only by the person's switch (`/access`, `replyAgent`), so a session with the switch off
 * never asks a reply-agent route; the API refuses them regardless (`WORK_ROUTE_MATRIX`).
 */
import { useQueries, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { type InboxAgentState, InboxAgentStatesSchema } from "../../src/inbox-v2/agent-contract.ts";
import { INBOX_PAGE_SIZE } from "../../src/inbox-v2/contract.ts";
import { DetailSchema } from "./detail-schema.ts";
import { request } from "./page-request.ts";

const workspaceApi = (workspaceId: string) =>
  `/api/work/workspaces/${encodeURIComponent(workspaceId)}`;

/** How often a conversation the agent is working on is read again: the reply agent's own pace. */
const AGENT_REFRESH_MS = 15_000;

// Open tabs can outlive a deploy: hide only an unfamiliar chip, preserving the other rows.
const AgentRowReadSchema = InboxAgentStatesSchema.shape.conversations.element
  .extend({
    state: InboxAgentStatesSchema.shape.conversations.element.shape.state.catch(null),
    // Older servers omit the flag; retain their drafting/sending polling behavior.
    workInFlight: z.boolean().optional(),
  })
  .strip();
const AgentStatesReadSchema = z.object({ conversations: z.array(AgentRowReadSchema) });

/**
 * The open conversation as the reply agent reads it (`GET …/plans/:id`), under the reply agent
 * inbox's own cache key, so both screens share one copy.
 */
export function useAgentDetail(workspaceId: string, planId: string, enabled: boolean) {
  return useQuery({ ...agentDetailQuery(workspaceId, planId), enabled });
}

/*
 * The open conversation read again on the agent's pace only while its work is moving: drafting,
 * sending, an answer it is carrying, a redraft awaited (W5, F17). Settled work is not polled; new
 * work on it shows through the inbox's arrival check, which reads this plan again when the
 * workspace moved. A second reader of the same plan, so the card that knows the work polls it.
 */
export function useAgentPolling(workspaceId: string, planId: string, active: boolean): void {
  useQuery({
    ...agentDetailQuery(workspaceId, planId),
    enabled: active,
    refetchInterval: active ? AGENT_REFRESH_MS : false,
    notifyOnChangeProps: [],
  });
}

/** Whether the agent is still working on a conversation, by its row and its own reading. */
export function workInFlight(
  state: InboxAgentState | null,
  detail: z.infer<typeof DetailSchema> | undefined,
): boolean {
  if (state === "drafting" || state === "sending") return true;
  if (detail === undefined) return false;
  return (detail.inFlight !== null && !detail.inFlight.failed) || detail.redraftSince !== null;
}

/** The open conversation's read as the reply agent makes it, for a prefetch to share its key. */
export const agentDetailQuery = (workspaceId: string, planId: string) => ({
  queryKey: ["plan", workspaceId, planId],
  /*
   * No freshness window: the agent's state moves on its own (drafting, sending), and its cards read
   * their plan again when opened. A neighbour read ahead with these same options paints at once and
   * is checked on open; the inbox thread beside it is reused for 15 s (`queries.ts`).
   */
  staleTime: 0,
  queryFn: async () =>
    DetailSchema.parse(
      await request(`${workspaceApi(workspaceId)}/plans/${encodeURIComponent(planId)}`),
    ),
});

/**
 * The agent's state for the rows on screen (`GET …/inbox/agent?ids=`), keyed by conversation. Read
 * again on the agent's pace while any row has work in flight, even beneath a ready-call chip.
 * Other changes follow something the rep did, which reads it again.
 *
 * The route takes at most `INBOX_PAGE_SIZE` ids, so each group is asked for in requests of that size.
 * A caller keeps what is stable apart from what comes and goes (the page's rows, the opened
 * conversation): opening one from off the page adds a request of its own and leaves the rows'
 * request, its cache entry and its chips as they were.
 */
export function useAgentStates(
  workspaceId: string,
  groups: readonly (readonly string[])[],
  enabled: boolean,
) {
  const requests = groups.flatMap((group) => {
    const unique = [...new Set(group)].sort();
    return Array.from({ length: Math.ceil(unique.length / INBOX_PAGE_SIZE) }, (_, chunk) =>
      unique.slice(chunk * INBOX_PAGE_SIZE, (chunk + 1) * INBOX_PAGE_SIZE).join(","),
    );
  });
  const queries = useQueries({
    queries: [...new Set(requests)].map((asked) => ({
      queryKey: ["agent", workspaceId, "states", asked],
      enabled,
      queryFn: async () =>
        AgentStatesReadSchema.parse(
          await request(
            `${workspaceApi(workspaceId)}/inbox/agent?ids=${encodeURIComponent(asked)}`,
          ),
        ),
      // A new page keeps the chips it had until its own arrive; a row not yet read has none.
      placeholderData: (previous: z.infer<typeof AgentStatesReadSchema> | undefined) => previous,
      refetchInterval: (current: {
        state: { data: z.infer<typeof AgentStatesReadSchema> | undefined };
      }) =>
        current.state.data?.conversations.some(
          (row) => row.workInFlight ?? (row.state === "drafting" || row.state === "sending"),
        )
          ? AGENT_REFRESH_MS
          : false,
    })),
  });
  const states = new Map<string, InboxAgentState | null>();
  if (enabled)
    for (const query of queries)
      for (const row of query.data?.conversations ?? []) states.set(row.id, row.state);
  return states;
}
